/**
 * CMS API client 核心層：fetch wrapper + 錯誤型別 + 共用 query 組裝。
 *
 * 這裡只放「跨領域共用」的東西。領域端點請放到同目錄下的各領域檔
 * （spots / products / partners …），再由 `../api-client` barrel 統一對外
 * 曝光——42 個頁面 import 的都是那條路徑，切檔時不應要求它們改 import。
 *
 * - dev: VITE_API_BASE=http://localhost:3000/api/v1
 * - 401（認證失敗）→ clearAccessToken() → 廣播 logout 事件；帶型錄碼的 401 不登出
 * - Problem Details (RFC 7807) 之 error body 保留於 ApiError.problem
 */

import type { components } from '../api-schema';
import { clearAccessToken, getAccessToken } from '../auth';

export const API_BASE: string = import.meta.env.VITE_API_BASE ?? 'http://localhost:3000/api/v1';

/**
 * 由 `docs/api/openapi.yaml` 自動產生的型別（`pnpm --filter @boat/cms api:types`）。
 *
 * 回應型別一律取自這裡，不要再手抄 —— 手抄的版本會悄悄漂移：`quota_per_month`
 * 是 bigint（後端回字串）卻曾被標成 number，`.toLocaleString()` 因此靜默失效。
 *
 * 尚未補 response DTO 的模組在 spec 上沒有 schema，那些型別暫時仍為手寫並在
 * 該處註明；後端補上 DTO 後即可比照改為 re-export。
 */
export type Schemas = components['schemas'];

export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  [key: string]: unknown;
}

export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly status: number;
  readonly problem?: ProblemDetails;

  constructor(status: number, message: string, problem?: ProblemDetails) {
    super(message);
    this.status = status;
    if (problem !== undefined) {
      this.problem = problem;
    }
  }
}

export interface ApiFetchInit extends Omit<RequestInit, 'body'> {
  body?: unknown;
  skipAuth?: boolean;
}

/**
 * 由錯誤回應取出可讀訊息。
 *
 * 後端的 BoatErrorFilter 走的是 `{ code, message, correlationId, path }`，不是
 * RFC7807 的 `detail` / `title`。只讀後者的話，所有錯誤都會退化成裸的
 * 「HTTP 400」，後端明明附了「partner_code 已存在：xxx」也看不到。
 *
 * `message` 有三種形狀，全部都要接：
 *   - 字串——`NotFoundException({ code, message })` 這類手寫的錯誤
 *   - 字串陣列——ValidationPipe 的欄位錯誤，併成一行
 *   - **多語系物件**——`BoatError` 的 `{ 'zh-TW', en, ja }`
 *
 * 第三種一度整個掉進 `HTTP ${status}`：`typeof {} === 'object'`，前兩個判斷都不
 * 成立。而 BoatError 正是「業務規則擋下來」那一類（編號重複、狀態不允許），也就
 * 是最需要把原因講清楚的一類——使用者看到的卻只有「HTTP 400」。
 *
 * 對外曝光是因為 XHR 上傳（見 `./media` 的 uploadMediaWithProgress）拿不到
 * Response 物件，錯誤訊息得自行由 responseText 組出來，行為必須與這裡一致。
 */
export function problemMessage(problem: ProblemDetails | undefined, status: number): string {
  if (problem) {
    if (typeof problem.detail === 'string' && problem.detail) return problem.detail;
    if (Array.isArray(problem.message) && problem.message.length > 0) {
      return problem.message.filter((m) => typeof m === 'string').join('；');
    }
    if (typeof problem.message === 'string' && problem.message) return problem.message;
    const localized = localizedText(problem.message);
    if (localized) return localized;
    if (typeof problem.title === 'string' && problem.title) return problem.title;
  }
  return `HTTP ${status}`;
}

/**
 * 多語系物件取一句話。CMS 介面是繁中，故繁中優先，取不到才退英日——寧可顯示
 * 英文，也不要顯示「HTTP 400」。
 */
function localizedText(value: unknown): string | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const map = value as Record<string, unknown>;
  for (const key of ['zh-TW', 'zh', 'en', 'ja']) {
    const text = map[key];
    if (typeof text === 'string' && text.trim() !== '') return text;
  }
  return null;
}

async function parseProblem(res: Response): Promise<ProblemDetails | undefined> {
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) {
    return undefined;
  }
  try {
    return (await res.json()) as ProblemDetails;
  } catch {
    return undefined;
  }
}

/**
 * 這個 401 是不是「你的登入失效了」。
 *
 * 兩種 401 長得不一樣：認證守衛擋下來的是 Nest 的 HttpException，`code` 一律是
 * `HTTP_ERROR`（Missing auth token / Invalid or expired session）；而領域錯誤是
 * BoatError，帶的是型錄碼（`RELEASE_MANIFEST_NO_ACTIVE_KEY` 這種）。
 *
 * 後者不該清掉 token：伺服器認得我們，問題出在別的地方（例如本機沒有可用的簽章
 * 金鑰）。把它當成登入失效的下場是使用者被丟回登入頁、重新登入、再按一次、再被
 * 丟回去——而真正的原因從頭到尾沒出現在畫面上。
 *
 * 判斷不出來時（沒有 code）一律當作登入失效：帶著一個死 token 停在原地，會讓
 * 每一支 API 都失敗卻沒有任何人把使用者導向登入頁，那比多登入一次難查得多。
 */
export function isAuthFailure(problem: ProblemDetails | undefined): boolean {
  const code = problem?.code;
  return code === undefined || code === 'HTTP_ERROR';
}

/**
 * 帶認證的 fetch，並在非 2xx 時丟出帶可讀訊息的 {@link ApiError}。
 *
 * 供「回應不是 JSON」的端點使用（檔案內容、CSV 匯出、multipart 上傳），
 * 這些無法走 {@link apiFetch}——它會嘗試解析 JSON。
 *
 * **401 一定要經由這裡**：漏掉 `clearAccessToken()` 會讓使用者帶著死 token
 * 停在原地，其他呼叫都會踢回登入、只有這支不會，看起來像隨機故障。
 */
export async function rawFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  const token = getAccessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers, credentials: 'include' });

  if (res.status === 401) {
    const problem = await parseProblem(res);
    if (isAuthFailure(problem)) clearAccessToken();
    throw new ApiError(401, problemMessage(problem, 401), problem);
  }
  if (!res.ok) {
    throw new ApiError(res.status, problemMessage(await parseProblem(res), res.status));
  }
  return res;
}

export async function apiFetch<T = unknown>(path: string, init: ApiFetchInit = {}): Promise<T> {
  const { body, skipAuth, headers: initHeaders, ...rest } = init;
  const headers = new Headers(initHeaders);

  if (body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }

  if (!skipAuth) {
    const token = getAccessToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  const requestInit: RequestInit = {
    ...rest,
    headers,
    credentials: 'include',
  };
  if (body !== undefined) {
    requestInit.body = typeof body === 'string' ? body : JSON.stringify(body);
  }

  const res = await fetch(`${API_BASE}${path}`, requestInit);

  if (res.status === 401) {
    const problem = await parseProblem(res);
    if (isAuthFailure(problem)) clearAccessToken();
    throw new ApiError(401, problemMessage(problem, 401), problem);
  }

  if (!res.ok) {
    const problem = await parseProblem(res);
    throw new ApiError(res.status, problemMessage(problem, res.status), problem);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

/**
 * 組 query string（含前導 `?`；無有效參數則回空字串）。
 *
 * 原本宣告在 pricing 段落中間，卻被 manual-review / reports / refunds /
 * rbac 等 7 處呼叫——單檔時靠 function hoisting 才沒出事。拆檔後它必須待在
 * 這裡，否則會變成領域模組之間的橫向依賴。
 *
 * 空字串與 undefined 皆視為「不帶此參數」（`''` 會讓後端收到空值而非略過）。
 */
export function buildQueryString(
  params: Record<string, string | number | boolean | undefined>,
): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}
